/**
 * Simple, reliable color utilities for the simplified design system.
 * These functions are intentionally minimal and bulletproof.
 */

/**
 * Convert hex color to HSL string format for CSS variables
 */
export function hexToHSL(hex: string): string {
  // Remove # if present
  const cleanHex = hex.replace('#', '');
  
  // Parse RGB values
  let r = 0, g = 0, b = 0;
  if (cleanHex.length === 3) {
    r = parseInt(cleanHex[0] + cleanHex[0], 16);
    g = parseInt(cleanHex[1] + cleanHex[1], 16);
    b = parseInt(cleanHex[2] + cleanHex[2], 16);
  } else if (cleanHex.length === 6) {
    r = parseInt(cleanHex.slice(0, 2), 16);
    g = parseInt(cleanHex.slice(2, 4), 16);
    b = parseInt(cleanHex.slice(4, 6), 16);
  } else {
    return '0 0% 50%'; // Fallback gray
  }

  // Convert to 0-1 range
  r /= 255;
  g /= 255;
  b /= 255;

  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  let h = 0;
  let s = 0;
  const l = (max + min) / 2;

  if (max !== min) {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);

    switch (max) {
      case r:
        h = ((g - b) / d + (g < b ? 6 : 0)) / 6;
        break;
      case g:
        h = ((b - r) / d + 2) / 6;
        break;
      case b:
        h = ((r - g) / d + 4) / 6;
        break;
    }
  }

  return `${Math.round(h * 360)} ${Math.round(s * 100)}% ${Math.round(l * 100)}%`;
}

/**
 * Determine if a color is "light" (needs dark text) or "dark" (needs light text)
 * Returns '#FFFFFF' for dark colors, '#000000' for light colors
 */
export function getContrastColor(hex: string): string {
  const cleanHex = hex.replace('#', '');

  let r = 0, g = 0, b = 0;
  if (cleanHex.length === 3) {
    r = parseInt(cleanHex[0] + cleanHex[0], 16);
    g = parseInt(cleanHex[1] + cleanHex[1], 16);
    b = parseInt(cleanHex[2] + cleanHex[2], 16);
  } else if (cleanHex.length === 6) {
    r = parseInt(cleanHex.slice(0, 2), 16);
    g = parseInt(cleanHex.slice(2, 4), 16);
    b = parseInt(cleanHex.slice(4, 6), 16);
  } else {
    return '#FFFFFF';
  }

  // Choose black or white by whichever gives the higher WCAG contrast ratio.
  // A simple lightness threshold defaults too eagerly to white, so mid and dark
  // saturated colors (e.g. a forest green or a strong blue) ended up with a
  // low-contrast white label. Comparing the actual contrast ratios (crossover
  // near luminance 0.179) always picks the more readable of the two.
  const toLinear = (v: number) => {
    const x = v / 255;
    return x <= 0.03928 ? x / 12.92 : Math.pow((x + 0.055) / 1.055, 2.4);
  };
  const luminance = 0.2126 * toLinear(r) + 0.7152 * toLinear(g) + 0.0722 * toLinear(b);
  const contrastWithBlack = (luminance + 0.05) / 0.05;
  const contrastWithWhite = 1.05 / (luminance + 0.05);
  return contrastWithBlack >= contrastWithWhite ? '#000000' : '#FFFFFF';
}

/**
 * Whether a hex color reads as "dark", i.e. it needs light text on top.
 * Delegates to getContrastColor (the WCAG contrast test) so the two always
 * agree.
 */
export function isDarkColor(hex: string): boolean {
  return getContrastColor(hex) === '#FFFFFF';
}

/**
 * Decide whether the public page should use the dark surface set.
 *
 * A custom page background overrides the light/dark toggle: the surfaces and
 * text then follow the real background brightness, so any background the
 * organizer picks stays readable (a light background never gets white text or
 * dark cards, and vice versa). With no custom background we simply honor the
 * theme toggle, so existing themed pages are unchanged.
 */
export function resolveIsDark(theme: 'light' | 'dark', backgroundColor?: string): boolean {
  const hasCustomBackground = !!backgroundColor && /^#[0-9A-Fa-f]{6}$/.test(backgroundColor);
  return hasCustomBackground ? isDarkColor(backgroundColor!) : theme === 'dark';
}

/**
 * Parse a hex color into numeric HSL components (h 0-360, s/l 0-100).
 */
function hexToHSLNumeric(hex: string): { h: number; s: number; l: number } {
  const m = hexToHSL(hex).match(/(\d+)\s+(\d+)%\s+(\d+)%/);
  if (!m) return { h: 0, s: 0, l: 50 };
  return { h: parseInt(m[1], 10), s: parseInt(m[2], 10), l: parseInt(m[3], 10) };
}

/**
 * Convert HSL (h 0-360, s/l 0-100) back to a #rrggbb hex string.
 */
function hslToHex(h: number, s: number, l: number): string {
  const sN = s / 100;
  const lN = l / 100;
  const k = (n: number) => (n + h / 30) % 12;
  const a = sN * Math.min(lN, 1 - lN);
  const f = (n: number) => lN - a * Math.max(-1, Math.min(k(n) - 3, Math.min(9 - k(n), 1)));
  const toHex = (x: number) => Math.round(255 * x).toString(16).padStart(2, '0');
  return `#${toHex(f(0))}${toHex(f(8))}${toHex(f(4))}`;
}

/**
 * WCAG relative luminance of a hex color (0 = black, 1 = white).
 */
function relativeLuminance(hex: string): number {
  const c = hex.replace('#', '');
  const toLin = (v: number) => {
    const x = v / 255;
    return x <= 0.03928 ? x / 12.92 : Math.pow((x + 0.055) / 1.055, 2.4);
  };
  const r = toLin(parseInt(c.slice(0, 2), 16));
  const g = toLin(parseInt(c.slice(2, 4), 16));
  const b = toLin(parseInt(c.slice(4, 6), 16));
  return 0.2126 * r + 0.7152 * g + 0.0722 * b;
}

/**
 * WCAG contrast ratio between two hex colors (1:1 .. 21:1).
 */
function contrastRatio(a: string, b: string): number {
  const la = relativeLuminance(a);
  const lb = relativeLuminance(b);
  const hi = Math.max(la, lb);
  const lo = Math.min(la, lb);
  return (hi + 0.05) / (lo + 0.05);
}

/** Shift a color's lightness by `delta` percentage points, preserving hue/sat. */
function shiftLightness(hex: string, delta: number): string {
  const { h, s, l } = hexToHSLNumeric(hex);
  return hslToHex(h, s, Math.max(0, Math.min(100, l + delta)));
}

/** Darken a color until it has at least `target` contrast against white. */
function darkenForContrastOnWhite(hex: string, target: number): string {
  const { h, s } = hexToHSLNumeric(hex);
  let l = hexToHSLNumeric(hex).l;
  let out = hslToHex(h, s, l);
  let guard = 0;
  while (l > 0 && contrastRatio(out, '#ffffff') < target && guard < 60) {
    l -= 2;
    out = hslToHex(h, s, l);
    guard++;
  }
  return out;
}

/**
 * Nudge a color's lightness toward whichever end (lighter or darker) reaches at
 * least `target` contrast against `bg`. A dark background pulls the color
 * lighter; a light background pulls it darker. This lets a highlight sit on the
 * chrome whether the chrome ends up light or dark.
 */
function adjustForContrastOn(hex: string, bg: string, target: number): string {
  const towardLight = isDarkColor(bg); // dark bg -> lighten the color, and vice versa
  const { h, s } = hexToHSLNumeric(hex);
  let l = hexToHSLNumeric(hex).l;
  let out = hslToHex(h, s, l);
  let guard = 0;
  while (l >= 0 && l <= 100 && contrastRatio(out, bg) < target && guard < 60) {
    l += towardLight ? 2 : -2;
    if (l < 0 || l > 100) break;
    out = hslToHex(h, s, l);
    guard++;
  }
  return out;
}

/** Normalize any hex (with/without `#`, 3 or 6 digit) to an upper-case `#RRGGBB`. */
function normalizeHex6(hex: string): string {
  let h = (hex || '').trim().replace(/^#/, '');
  if (h.length === 3) h = h[0] + h[0] + h[1] + h[1] + h[2] + h[2];
  if (!/^[0-9a-fA-F]{6}$/.test(h)) return '#000000';
  return `#${h.toUpperCase()}`;
}

/** Blend two hex colors. `t` is the weight of `a` (t=1 -> a, t=0 -> b). */
function mixHex(a: string, b: string, t: number): string {
  const pa = normalizeHex6(a).slice(1);
  const pb = normalizeHex6(b).slice(1);
  const ch = (s: string, i: number) => parseInt(s.slice(i, i + 2), 16);
  const out = [0, 2, 4].map((i) => {
    const v = Math.round(ch(pa, i) * t + ch(pb, i) * (1 - t));
    return Math.max(0, Math.min(255, v)).toString(16).padStart(2, '0');
  });
  return `#${out.join('')}`.toUpperCase();
}

/**
 * The card "surface" for a page background: the background nudged a few percent
 * toward its own ink (near-black on a light page, white on a dark page) so cards
 * separate from the page without a hardcoded light/dark value. `elevated` pushes
 * a touch further, for the subtle `--muted` fill (hover rows). This is the Brand
 * Theming model, so a league's cards always sit just off whatever background the
 * organizer picks, tinted background and all.
 */
function deriveSurface(background: string, elevated = false): string {
  const ink = getContrastColor(background);
  const dark = isDarkColor(background);
  const t = elevated ? (dark ? 0.12 : 0.08) : (dark ? 0.07 : 0.05);
  return mixHex(ink, background, t);
}

/** Muted ink for secondary text on a surface: the surface's ink blended 58% toward it. */
function deriveMutedInk(surface: string): string {
  return mixHex(getContrastColor(surface), surface, 0.58);
}

/** Faint hairline border for a surface: the surface's ink blended 16% toward it. */
function deriveBorder(surface: string): string {
  return mixHex(getContrastColor(surface), surface, 0.16);
}

/**
 * The auto-contrast text colors for a chrome/ink surface: a main foreground
 * (black or white, whichever is readable) plus two muted steps for secondary
 * and tertiary labels (blended toward the surface). Because the text is derived
 * from the surface, any brand color the organizer picks stays readable, which
 * is exactly what the customization editor promises ("pick your colors, the
 * text color is chosen automatically").
 */
export function deriveChromeText(surface: string) {
  const fg = getContrastColor(surface);
  return {
    fg,
    muted: mixHex(fg, surface, 0.68),
    faint: mixHex(fg, surface, 0.5),
  };
}

/**
 * Derive the ESPN-style public-hub chrome tokens from the organizer's brand
 * colors so the white-label theme picker recolors the whole public hub
 * (masthead, nav, ticker, accent bars, nav underline), not just the shadcn
 * widgets. Returned values are raw hex, matching the `--espn-*` defaults in
 * index.css. `--espn-live` stays a fixed red (universal "live" signal).
 *
 * Mapping (Brand Theming model): the masthead/nav chrome is the league's
 * SECONDARY color (the "header / sidebar" field) and the accent (nav underline,
 * active states, crest, PO tags) is the PRIMARY color (the "buttons / accents"
 * field). The chrome is shown faithfully (not forced dark); its text color is
 * auto-derived for contrast (`--espn-fg*` via `getContrastColor`), so a light
 * header shows as a light masthead with dark text and a dark header as a dark
 * masthead with light text. That is what makes changing the header color
 * actually recolor the page the way organizers expect.
 */
export function deriveEspnChrome(chromeColor: string, accentColor: string) {
  const chrome = normalizeHex6(chromeColor);
  const text = deriveChromeText(chrome);
  // "Deeper" bar (ticker/pills): a touch further from the text color so it reads
  // as a shade off the masthead while staying on the same side of the light/dark
  // line, so the shared text color stays readable on both.
  const deeper = text.fg === '#FFFFFF' ? '#000000' : '#FFFFFF';
  const accent = adjustForContrastOn(accentColor, chrome, 3.2);
  return {
    chrome,
    // Mostly the chrome with a small push (toward the far side of the text) so
    // the ticker/pills read as a shade off the masthead, not a different color.
    ink: mixHex(chrome, deeper, 0.88),
    lineD: mixHex(chrome, text.fg, 0.84), // hairline divider: a small step toward the text
    fg: text.fg, // main text on the chrome/ticker
    fgMuted: text.muted, // secondary text (season name, inactive tabs, records)
    fgFaint: text.faint, // tertiary labels (date/time, "Final", losing score)
    // Highlight (nav underline, PO tags) from the accent/primary color, pushed to
    // contrast against the chrome whichever way the chrome leans.
    accent,
    accentForeground: getContrastColor(accent), // readable text on the accent
    // Brand accent used as text/bars on the white cards: keep it readable.
    accentInk: darkenForContrastOnWhite(accentColor, 4.6),
  };
}

/** Public WCAG contrast ratio (1..21) between two hex colors, for readability badges. */
export function getContrastRatio(a: string, b: string): number {
  return contrastRatio(a, b);
}

/**
 * The full readable token set for a brand, keyed by role. Text colors are always
 * auto-derived for contrast, and cards/borders come from the page background.
 */
export interface BrandTokens {
  primary: string;
  secondary: string;
  background: string;
  onPrimary: string; // text on primary buttons/accents
  onSecondary: string; // text on the secondary header/chrome
  onBackground: string; // page text
  surface: string; // card background, derived from the page background
  onSurface: string; // card text
  muted: string; // subtle elevated fill (hover rows)
  mutedOnBackground: string; // secondary text on the page
  mutedOnSurface: string; // secondary text on cards
  mutedOnSecondary: string; // secondary text on the header (season name)
  faintOnSecondary: string; // tertiary text on the header (inactive tabs)
  border: string; // hairline border, derived from the page background
  primarySoft: string; // soft primary tint for a highlighted row on a surface
  accentOnSurface: string; // primary made readable as text/bars on a card
  accentOnBackground: string; // primary made readable as text on the page
  // Masthead chrome (header = secondary, accent = primary), see deriveEspnChrome.
  chrome: string;
  chromeInk: string;
  chromeLine: string;
  accent: string;
  accentForeground: string;
  accentInk: string;
}

/**
 * Derive the full Brand Theming token set from the organizer's three inputs.
 * This is the single source of truth the live public page also uses (via
 * `generateSimpleCSS` + `deriveEspnChrome`), so the editor's live preview and
 * its readability badges reflect exactly what ships:
 * - the header/nav (chrome) is the SECONDARY color, the PRIMARY color is the
 *   accent (buttons, nav underline, active states, crest),
 * - every text color is auto-derived for contrast, and
 * - cards/borders are derived from the page background.
 */
export function deriveBrandTokens(primaryRaw: string, secondaryRaw: string, backgroundRaw: string): BrandTokens {
  const primary = normalizeHex6(primaryRaw);
  const secondary = normalizeHex6(secondaryRaw);
  const background = normalizeHex6(backgroundRaw);
  const surface = deriveSurface(background);
  const espn = deriveEspnChrome(secondary, primary);
  return {
    primary,
    secondary,
    background,
    onPrimary: getContrastColor(primary),
    onSecondary: getContrastColor(secondary),
    onBackground: getContrastColor(background),
    surface,
    onSurface: getContrastColor(surface),
    muted: deriveSurface(background, true),
    mutedOnBackground: deriveMutedInk(background),
    mutedOnSurface: deriveMutedInk(surface),
    mutedOnSecondary: espn.fgMuted,
    faintOnSecondary: espn.fgFaint,
    border: deriveBorder(background),
    primarySoft: mixHex(primary, surface, 0.14),
    accentOnSurface: adjustForContrastOn(primary, surface, 4.5),
    accentOnBackground: adjustForContrastOn(primary, background, 4.5),
    chrome: espn.chrome,
    chromeInk: espn.ink,
    chromeLine: espn.lineD,
    accent: espn.accent,
    accentForeground: espn.accentForeground,
    accentInk: espn.accentInk,
  };
}

/**
 * Generate a hover color by adjusting lightness
 * For light colors (L > 50%), darken; for dark colors, lighten
 */
function generateHoverHSL(hslString: string): string {
  const parts = hslString.match(/(\d+)\s+(\d+)%\s+(\d+)%/);
  if (!parts) return hslString;
  
  const h = parseInt(parts[1]);
  const s = parseInt(parts[2]);
  const l = parseInt(parts[3]);
  
  // Adjust lightness: darken light colors, lighten dark colors
  const newL = l > 50 ? Math.max(l - 10, 20) : Math.min(l + 10, 80);
  
  return `${h} ${s}% ${newL}%`;
}

/**
 * Generate an active color (more pronounced than hover)
 */
function generateActiveHSL(hslString: string): string {
  const parts = hslString.match(/(\d+)\s+(\d+)%\s+(\d+)%/);
  if (!parts) return hslString;
  
  const h = parseInt(parts[1]);
  const s = parseInt(parts[2]);
  const l = parseInt(parts[3]);
  
  // Adjust lightness more than hover
  const newL = l > 50 ? Math.max(l - 15, 15) : Math.min(l + 15, 85);
  
  return `${h} ${s}% ${newL}%`;
}

/**
 * Generate simple CSS for the league customization
 * This is the core function - simple, reliable, no fancy calculations
 */
export function generateSimpleCSS(
  theme: 'light' | 'dark',
  primaryColor: string,
  secondaryColor: string,
  customCss?: string,
  backgroundColor?: string
): string {
  // Background defaults to the theme value, but the organizer can override it
  // with a custom page background color. When they do, the WHOLE surface set
  // (text, cards, muted, borders) and the light/dark class follow the actual
  // background's brightness instead of the theme toggle, so a light background
  // never ends up with white text or dark cards (and vice versa). With no
  // custom background this is exactly the theme toggle as before.
  const hasCustomBackground = !!backgroundColor && /^#[0-9A-Fa-f]{6}$/.test(backgroundColor);
  const isDark = resolveIsDark(theme, backgroundColor);
  const background = hasCustomBackground ? backgroundColor! : (theme === 'dark' ? '#0D0D0D' : '#FFFFFF');
  // Surface set. With a custom page background we derive the page text, cards,
  // muted fills, and borders FROM that background (the Brand Theming model), so
  // any background the organizer picks gets coherent, readable, tint-matched
  // cards and text. With no custom background we keep the fixed light/dark
  // surface set unchanged, so existing themed pages are untouched.
  const foreground = hasCustomBackground ? getContrastColor(background) : (isDark ? '#FFFFFF' : '#1A1A1A');
  const card = hasCustomBackground ? deriveSurface(background) : (isDark ? '#1A1A1A' : '#FFFFFF');
  const cardForeground = hasCustomBackground ? getContrastColor(card) : (isDark ? '#F5F5F5' : '#1A1A1A');
  const muted = hasCustomBackground ? deriveSurface(background, true) : (isDark ? '#1F1F1F' : '#F5F5F5');
  const mutedForeground = hasCustomBackground ? deriveMutedInk(card) : (isDark ? '#A0A0A0' : '#666666');
  const border = hasCustomBackground ? deriveBorder(background) : (isDark ? '#2A2A2A' : '#E5E5E5');
  const destructive = '#DC2626'; // Red for destructive actions
  
  // Get contrast colors for primary and secondary
  const primaryForeground = getContrastColor(primaryColor);
  const secondaryForeground = getContrastColor(secondaryColor);
  const destructiveForeground = '#FFFFFF';
  
  // Convert to HSL
  const primaryHSL = hexToHSL(primaryColor);
  const secondaryHSL = hexToHSL(secondaryColor);
  const destructiveHSL = hexToHSL(destructive);
  const accentHSL = primaryHSL; // Use primary as accent
  
  // Generate hover and active states
  const primaryHoverHSL = generateHoverHSL(primaryHSL);
  const primaryActiveHSL = generateActiveHSL(primaryHSL);
  const secondaryHoverHSL = generateHoverHSL(secondaryHSL);
  const secondaryActiveHSL = generateActiveHSL(secondaryHSL);
  const destructiveHoverHSL = generateHoverHSL(destructiveHSL);
  const destructiveActiveHSL = generateActiveHSL(destructiveHSL);
  const accentHoverHSL = generateHoverHSL(accentHSL);
  const accentActiveHSL = generateActiveHSL(accentHSL);

  // Derive the ESPN public-hub chrome from the brand colors so the theme picker
  // recolors the masthead, nav, ticker, and accents (kept as raw hex, like the
  // index.css defaults these override). The masthead/nav chrome is the SECONDARY
  // color and the accent is the PRIMARY color, matching the editor's field labels.
  const espn = deriveEspnChrome(secondaryColor, primaryColor);

  // Convert all colors to HSL for CSS variables
  const css = `
/* Generated by SimpleDesignEditor */
:root {
  --espn-ink: ${espn.ink};
  --espn-chrome: ${espn.chrome};
  --espn-line-d: ${espn.lineD};
  --espn-accent: ${espn.accent};
  --espn-accent-ink: ${espn.accentInk};
  --espn-fg: ${espn.fg};
  --espn-fg-muted: ${espn.fgMuted};
  --espn-fg-faint: ${espn.fgFaint};
  --espn-accent-foreground: ${espn.accentForeground};
  --background: ${hexToHSL(background)};
  --foreground: ${hexToHSL(foreground)};
  --primary: ${primaryHSL};
  --primary-foreground: ${hexToHSL(primaryForeground)};
  --primary-hover: ${primaryHoverHSL};
  --primary-active: ${primaryActiveHSL};
  --secondary: ${secondaryHSL};
  --secondary-foreground: ${hexToHSL(secondaryForeground)};
  --secondary-hover: ${secondaryHoverHSL};
  --secondary-active: ${secondaryActiveHSL};
  --muted: ${hexToHSL(muted)};
  --muted-foreground: ${hexToHSL(mutedForeground)};
  --card: ${hexToHSL(card)};
  --card-foreground: ${hexToHSL(cardForeground)};
  --popover: ${hexToHSL(card)};
  --popover-foreground: ${hexToHSL(cardForeground)};
  --border: ${hexToHSL(border)};
  --input: ${hexToHSL(border)};
  --ring: ${primaryHSL};
  --accent: ${accentHSL};
  --accent-foreground: ${hexToHSL(primaryForeground)};
  --accent-hover: ${accentHoverHSL};
  --accent-active: ${accentActiveHSL};
  --destructive: ${destructiveHSL};
  --destructive-foreground: ${hexToHSL(destructiveForeground)};
  --destructive-hover: ${destructiveHoverHSL};
  --destructive-active: ${destructiveActiveHSL};
}

${isDark ? '.dark, :root.dark {' : ':root:not(.dark) {'}
  --background: ${hexToHSL(background)};
  --foreground: ${hexToHSL(foreground)};
  --primary: ${primaryHSL};
  --primary-foreground: ${hexToHSL(primaryForeground)};
  --primary-hover: ${primaryHoverHSL};
  --primary-active: ${primaryActiveHSL};
  --secondary: ${secondaryHSL};
  --secondary-foreground: ${hexToHSL(secondaryForeground)};
  --secondary-hover: ${secondaryHoverHSL};
  --secondary-active: ${secondaryActiveHSL};
  --muted: ${hexToHSL(muted)};
  --muted-foreground: ${hexToHSL(mutedForeground)};
  --card: ${hexToHSL(card)};
  --card-foreground: ${hexToHSL(cardForeground)};
  --popover: ${hexToHSL(card)};
  --popover-foreground: ${hexToHSL(cardForeground)};
  --border: ${hexToHSL(border)};
  --input: ${hexToHSL(border)};
  --ring: ${primaryHSL};
  --accent: ${accentHSL};
  --accent-foreground: ${hexToHSL(primaryForeground)};
  --accent-hover: ${accentHoverHSL};
  --accent-active: ${accentActiveHSL};
  --destructive: ${destructiveHSL};
  --destructive-foreground: ${hexToHSL(destructiveForeground)};
  --destructive-hover: ${destructiveHoverHSL};
  --destructive-active: ${destructiveActiveHSL};
}

${customCss || ''}
`;

  return css;
}
